import type { DashboardProject } from '../project/types/project';
import type { PipeOption, CategoryFieldConfig } from '../ndt/components/FilterPanel';
import { FilterPanel } from '../ndt/components/FilterPanel';
import { VisualizationPanel, WelderDashboard } from '../ndt/components/ProjectDetailView';
import { NdtStatisticsSection } from '../ndt/components/NdtStatisticsSection';
import { WeldDashboard } from '../weld-analytics/WeldAnalytics';
import type { WeldDetailExtras } from '../weld-analytics/components/WeldDetailView';
import { sectionTitleClass } from '../weld-analytics/components/dashboard/dashboardTokens';
import { CombinedNdtInsights } from './CombinedNdtInsights';
import { useCombinedNdtData } from './hooks/useCombinedNdtData';

export interface CombinedAnalyticsHostProps {
  project: DashboardProject;
  pipeIds: string[];
  pipeOptions?: PipeOption[];
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
}

const NDT_CATEGORY_FIELDS: CategoryFieldConfig[] = [
  { key: 'wallThickness', label: 'WT', title: 'Wall thickness', categoryKey: 'wall_thickness' },
  { key: 'location', label: 'Location', categoryKey: 'location' },
];

function NdtStatus({
  status,
  error,
  emptyText,
  onRetry,
}: {
  status: string;
  error: string | null;
  emptyText: string;
  onRetry: () => void;
}) {
  if (status === 'error') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
        <p className="text-sm text-readable-red">{error || 'NDT data failed to load.'}</p>
        <button
          type="button"
          className="mt-3 text-sm font-medium text-blueprint-blue underline"
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    );
  }
  if (status === 'loading') {
    return <p className="text-sm text-heritage-grey">Loading NDT data…</p>;
  }
  if (status === 'idle') {
    return <p className="text-sm text-heritage-grey">Select a pipe size to load NDT data.</p>;
  }
  if (status === 'empty') {
    return <p className="text-sm text-heritage-grey">{emptyText}</p>;
  }
  return null;
}

export function CombinedAnalyticsHost({
  project,
  pipeIds,
  pipeOptions = [],
  filtersOpen = false,
  onFiltersOpenChange,
}: CombinedAnalyticsHostProps) {
  const projectId = project?.project?.projectId ?? null;
  const ndt = useCombinedNdtData({
    projectId,
    pipeIds,
    pipeOptionCount: pipeOptions.length,
  });

  const detailExtras: WeldDetailExtras = {
    overviewNdt: {
      status: ndt.summaryStatus,
      error: ndt.summaryError,
      onRetry: ndt.retry,
    },
    filterFields: (
      <FilterPanel
        inline
        filters={null}
        selection={ndt.selection}
        onChange={ndt.onFilterChange}
        hideProjectFilter
        preserveProjectOnClear
        categoryData={ndt.categoryData}
        categoryFields={NDT_CATEGORY_FIELDS}
        pipeOptions={[]}
      />
    ),
    overviewTrail: (
      <section aria-label="NDT visualizations" className="space-y-3" data-testid="combined-ndt-visualizations">
        <h2 className={sectionTitleClass}>NDT visualizations</h2>
        {ndt.summaryStatus === 'ready' && ndt.summary ? (
          <VisualizationPanel histograms={ndt.summary.histograms ?? {}} insights={ndt.summary.insights} />
        ) : (
          <NdtStatus
            status={ndt.summaryStatus}
            error={ndt.summaryError}
            emptyText="No visualization data available."
            onRetry={ndt.retry}
          />
        )}
      </section>
    ),
    statisticsTabs: [
      {
        label: 'NDT statistics',
        content: (
          <section aria-label="NDT statistics" className="space-y-3" data-testid="combined-ndt-statistics">
            {ndt.summaryStatus === 'ready' && ndt.summary ? (
              <NdtStatisticsSection data={ndt.summary} />
            ) : (
              <NdtStatus
                status={ndt.summaryStatus}
                error={ndt.summaryError}
                emptyText="No NDT statistics for this scope."
                onRetry={ndt.retry}
              />
            )}
          </section>
        ),
      },
      {
        label: 'Welder performance',
        content: (
          <section aria-label="Welder performance" className="space-y-3" data-testid="combined-welder-performance">
            <h2 className={sectionTitleClass}>Welder performance</h2>
            {ndt.summaryStatus === 'ready' && ndt.summary ? (
              <WelderDashboard
                welderPerformance={ndt.summary.insights?.welderPerformance}
                welderDetailedAnalysis={ndt.summary.insights?.welderDetailedAnalysis}
              />
            ) : (
              <NdtStatus
                status={ndt.summaryStatus}
                error={ndt.summaryError}
                emptyText="No welder performance data for this scope."
                onRetry={ndt.retry}
              />
            )}
          </section>
        ),
      },
    ],
    insightsTrail: (
      <section
        aria-label="NDT insights"
        className="space-y-4 border-t border-[#E2E5EA] pt-6"
        data-testid="combined-ndt-insights"
      >
        <h2 className={sectionTitleClass}>NDT insights</h2>
        <CombinedNdtInsights
          classicInsights={(ndt.summary?.aiInsights as never) || null}
          classicStatus={ndt.summaryStatus}
          classicError={ndt.summaryError}
          mlInsights={(ndt.insights as never) || null}
          mlStatus={ndt.insightsStatus}
          mlError={ndt.insightsError}
          onRetry={ndt.retry}
        />
      </section>
    ),
  };

  return (
    <WeldDashboard
      project={project}
      pipeIds={pipeIds}
      pipeOptions={pipeOptions}
      detailExtras={detailExtras}
      filtersOpen={filtersOpen}
      onFiltersOpenChange={onFiltersOpenChange}
    />
  );
}
